import { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { AnimatePresence } from 'motion/react'

import { store } from '@/src/stores/store'
import InactivityModal from './InactivityModal'
import { useSvidQuery } from '@/src/hooks/useSvidQuery'

const INACTIVITY_TIMEOUT_MS = 3 * 60 * 1000
const COUNTDOWN_SECONDS = 30
const ACTIVITY_EVENTS = ['pointerdown', 'touchstart', 'mousedown', 'keydown'] as const

const InactivityGuard = observer(function InactivityGuard() {
    const { isPresent: isStele } = useSvidQuery()
    const [isOpen, setOpen] = useState(false)
    const isEnabled = isStele && store.appFlowPhase !== 'start' && store.appFlowPhase !== 'splash'

    useEffect(() => {
        if (!isEnabled || isOpen) return

        let timeoutId = 0

        const schedule = () => {
            timeoutId = window.setTimeout(() => setOpen(true), INACTIVITY_TIMEOUT_MS)
        }

        const handleActivity = () => {
            window.clearTimeout(timeoutId)
            schedule()
        }

        schedule()
        ACTIVITY_EVENTS.forEach((eventName) =>
            window.addEventListener(eventName, handleActivity, { passive: true }),
        )

        return () => {
            window.clearTimeout(timeoutId)
            ACTIVITY_EVENTS.forEach((eventName) =>
                window.removeEventListener(eventName, handleActivity),
            )
        }
    }, [isEnabled, isOpen])

    const handleContinue = useCallback(() => setOpen(false), [])

    const handleEnd = useCallback(() => {
        setOpen(false)
        store.resetGame()
    }, [])

    return (
        <AnimatePresence>
            {isEnabled && isOpen ? (
                <InactivityModal
                    key='inactivity-modal'
                    countdownSeconds={COUNTDOWN_SECONDS}
                    onContinue={handleContinue}
                    onEnd={handleEnd}
                />
            ) : null}
        </AnimatePresence>
    )
})

export default InactivityGuard
